import React, { lazy, ReactNode, Suspense } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes as RouterRoutes,
} from 'react-router-dom';
import { lazily } from 'react-lazily';
import { isCapacitor } from '@zignaly-open/ui';
import AuthenticatedWall from 'util/walls/AuthenticatedWall';
import UnauthenticatedWall from './util/walls/UnauthenticatedWall';
import ServiceOwnerWall from './util/walls/ServiceOwnerWall';
import * as Routes from './routes';

// views we load unconditionally
import Login from './views/Auth/Login';
import Signup from './views/Auth/Signup';
import SignupPlain from './views/Auth/SignupPlain';
import { zigSuspenseFallback } from 'util/suspense';
import {
  isAccountRequired,
  isFeatureOn,
  isZignaly,
  supportsNonCryptoServices,
  useZIndexes,
} from './whitelabel';
import { Features } from './whitelabel/type';

const NotFound = lazy(() => import('./views/Error/404'));
const TermsOfService = lazy(() => import('./views/Legal/TermsOfService'));
const Privacy = lazy(() => import('./views/Legal/Privacy'));
const MobileLanding = lazy(() => import('./views/Auth/MobileLanding'));
const ServiceHeader = lazy(
  () => import('./views/TraderService/components/ServiceHeader'),
);
const ProfitSharing = lazy(() => import('./views/ProfitSharing'));
const MarketplacesZIndex = lazy(
  () => import('./views/ProfitSharing/MarketplaceZIndex'),
);
const ProfitSharingRwa = lazy(() => import('./views/ProfitSharing'));
const ForgotPassword = lazy(() => import('./views/Auth/ForgotPassword'));
const HelpInvestor = lazy(() => import('./views/Help/HelpInvestor'));
const ResetPassword = lazy(() => import('views/Auth/ResetPassword'));
const Referrals = lazy(() => import('./views/Referrals'));
const Invite = lazy(() => import('./views/Referrals/Invite'));
const Subscriptions = lazy(() => import('./views/Subscriptions'));

const { default: Dashboard, DashboardModalInvestmentEdit } = lazily(
  () => import('./views/Dashboard'),
);

const {
  Management,
  Investors,
  BecomeTrader,
  Positions,
  Coins,
  ServiceApi,
  Signals,
  EditService,
  Manual,
} = lazily(() => import('./views/TraderService/routes'));

const { Kyc, KycNew, SettingsHeader, UpdatePassword, Toggle2FA, EditProfile } =
  lazily(() => import('./views/Settings/routes'));

const {
  default: MyBalances,
  MyBalanceReportDownload,
  MyBalancesDeposit,
} = lazily(() => import('./views/Balance'));

const { default: ServiceProfile, ServiceProfileInvestment } = lazily(
  () => import('./views/TraderService/ServiceProfile'),
);

const Sentry = await import('@sentry/react');

const outleted = (Component: ReactNode) => (
  <>
    {Component}
    <Suspense fallback={zigSuspenseFallback}>
      <Outlet />
    </Suspense>
  </>
);
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(RouterRoutes);

const Router: React.FC = () => {
  return (
    <SentryRoutes>
      <Route element={<AuthenticatedWall />}>
        <Route path={Routes.ROUTE_DASHBOARD} element={outleted(<Dashboard />)}>
          <Route
            path={Routes.ROUTE_DASHBOARD_EDIT_INVESTMENT}
            element={
              <DashboardModalInvestmentEdit bgRoute={Routes.ROUTE_DASHBOARD} />
            }
          />
        </Route>
        <Route
          path={Routes.ROUTE_MY_BALANCES}
          element={outleted(<MyBalances />)}
        >
          <Route
            path={Routes.ROUTE_MY_BALANCES_DEPOSIT}
            element={<MyBalancesDeposit bgRoute={Routes.ROUTE_MY_BALANCES} />}
          />
          <Route
            path={Routes.ROUTE_MY_BALANCES_DEPOSIT_COIN}
            element={<MyBalancesDeposit bgRoute={Routes.ROUTE_MY_BALANCES} />}
          />
        </Route>
        <Route
          path={Routes.ROUTE_MY_BALANCES_TRANSACTIONS}
          element={outleted(<MyBalances />)}
        />
        {isFeatureOn(Features.Subscriptions) && (
          <Route
            path={Routes.ROUTE_SUBSCRIPTIONS}
            element={<Subscriptions />}
          />
        )}
        {isFeatureOn(Features.Referrals) && (
          <Route path={Routes.ROUTE_REFERRALS} element={<Referrals />} />
        )}
        {isFeatureOn(Features.Kyc) && isZignaly && (
          <Route path={Routes.ROUTE_KYC_LANDING} element={<KycNew />} />
        )}
      </Route>

      <Route element={outleted(<ServiceHeader />)}>
        <Route
          path={Routes.ROUTE_TRADING_SERVICE}
          element={outleted(<ServiceProfile />)}
        >
          <Route
            path={Routes.ROUTE_PROFIT_SHARING_SERVICE_INVEST}
            element={
              <ServiceProfileInvestment
                bgRoute={Routes.ROUTE_TRADING_SERVICE}
              />
            }
          />
        </Route>
        <Route element={<ServiceOwnerWall />}>
          <Route
            path={Routes.ROUTE_TRADING_SERVICE_MANAGE}
            element={<Management />}
          />
          <Route
            path={Routes.ROUTE_TRADING_SERVICE_INVESTORS}
            element={<Investors />}
          />
          <Route
            path={Routes.ROUTE_TRADING_SERVICE_POSITIONS}
            element={<Positions />}
          />
          <Route
            path={Routes.ROUTE_TRADING_SERVICE_COINS}
            element={<Coins />}
          />
          <Route
            path={Routes.ROUTE_TRADING_SERVICE_MANUAL}
            element={<Manual />}
          />
          <Route
            path={Routes.ROUTE_TRADING_SERVICE_API}
            element={<ServiceApi />}
          />
          <Route
            path={Routes.ROUTE_TRADING_SERVICE_SIGNALS}
            element={<Signals />}
          />
          <Route
            path={Routes.ROUTE_TRADING_SERVICE_EDIT}
            element={<EditService />}
          />
        </Route>
      </Route>

      <Route element={outleted(<SettingsHeader />)}>
        <Route element={<AuthenticatedWall />}>
          {isFeatureOn(Features.Kyc) && (
            <Route path={Routes.ROUTE_KYC} element={<Kyc />} />
          )}
          <Route path={Routes.ROUTE_2FA} element={<Toggle2FA />} />
          <Route path={Routes.ROUTE_EDIT_PROFILE} element={<EditProfile />} />
          <Route path={Routes.ROUTE_PASSWORD} element={<UpdatePassword />} />
        </Route>
      </Route>

      {isFeatureOn(Features.CreateService) && (
        <Route path={Routes.ROUTE_BECOME_TRADER} element={<BecomeTrader />} />
      )}

      <Route path={Routes.ROUTE_REPORT} element={<MyBalanceReportDownload />} />
      <Route path={Routes.ROUTE_HELP_INVESTOR} element={<HelpInvestor />} />

      <Route element={<UnauthenticatedWall />}>
        <Route path={Routes.ROUTE_REFERRALS_INVITE} element={<Invite />} />
        <Route
          path={Routes.ROUTE_REFERRALS_INVITE_SHORT}
          element={<Invite />}
        />
        <Route path={Routes.ROUTE_LOGIN} element={<Login />} />
        {isCapacitor && (
          <Route
            path={Routes.ROUTE_MOBILE_LANDING}
            element={<MobileLanding />}
          />
        )}

        {/* Waaaaait shouldn't we hide this if we have HideSignup feature on? */}
        {/* Turns out we shouldn't lol */}
        <Route
          path={Routes.ROUTE_SIGNUP}
          element={
            isFeatureOn(Features.NewSignup) ? <Signup /> : <SignupPlain />
          }
        />

        {/* Direct link access only to increase sign-ups and reduce friction during the sign-up process */}
        {/* Light version of Signup form */}
        <Route
          path={Routes.ROUTE_SIGNUP_LIGHT}
          element={
            isFeatureOn(Features.NewSignup) ? (
              <Signup colorSchema={'light'} />
            ) : (
              <SignupPlain />
            )
          }
        />

        <Route
          path={Routes.ROUTE_FORGOT_PASSWORD}
          element={<ForgotPassword />}
        />
        <Route path={Routes.ROUTE_RESET_PASSWORD} element={<ResetPassword />} />
      </Route>

      <Route element={isAccountRequired ? <AuthenticatedWall /> : <Outlet />}>
        {isZignaly && useZIndexes && (
          <Route
            path={Routes.ROUTE_MARKETPLACE_Z_INDEXES}
            element={<MarketplacesZIndex />}
          />
        )}

        <Route
          path={Routes.ROUTE_PROFIT_SHARING}
          key={'profit-sharing'}
          element={<ProfitSharing />}
        />

        {supportsNonCryptoServices && (
          <Route
            key={'profit-sharing-rwa'}
            path={Routes.ROUTE_PROFIT_SHARING_RWA}
            element={<ProfitSharingRwa />}
          />
        )}
      </Route>

      <Route path={Routes.ROUTE_LEGAL_TOS} element={<TermsOfService />} />
      <Route path={Routes.ROUTE_LEGAL_PRIVACY} element={<Privacy />} />
      <Route path={Routes.ROUTE_404} element={<NotFound />} />

      {/* Sometimes the CF challenge redirects to /index.html. Let's take care of that */}
      <Route path={'/index.html'} element={<Navigate to={'/'} replace />} />

      <Route
        path='/'
        element={
          <Navigate
            to={
              isCapacitor
                ? Routes.ROUTE_MOBILE_LANDING
                : Routes.ROUTE_MAIN_MARKETPLACE
            }
            replace
          />
        }
      />
      <Route path='*' element={<Navigate to={Routes.ROUTE_404} replace />} />
    </SentryRoutes>
  );
};

export default Router;
